<script setup>
// Assistente de IA — as chaves de API do módulo.
//
// A chave nunca volta do servidor: o que se vê é a máscara. Por isso o campo de edição avisa
// que deixá-lo em branco mantém a chave guardada.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabelaVazia from '../components/TabelaVazia.vue';

const { t } = useI18n();
const { carregando, erro, chamar } = useAssistenteIaApi();

const credenciais = ref([]);
const emEdicao = ref(null);
const salvando = ref(false);
const testando = ref(null);
const resultadoDoTeste = ref(null);
const paraApagar = ref(null);

const vazia = () => ({
  id: null,
  provedor: 'anthropic',
  finalidade: 'texto',
  rotulo: '',
  chave_api: '',
  configuracao: { modelo_padrao: '', endpoint: '' },
});

// Na Fase 1, vetorização só existe na OpenAI — o formulário acompanha isso em vez de deixar a
// pessoa descobrir pelo erro do servidor.
const finalidadesDisponiveis = computed(() =>
  emEdicao.value?.provedor === 'openai' ? ['texto', 'vetorizacao'] : ['texto']
);

const carregar = async () => {
  const dados = await chamar(assistenteIaApi.credenciais());
  if (dados) credenciais.value = dados.payload;
};

const abrirNova = () => {
  resultadoDoTeste.value = null;
  emEdicao.value = vazia();
};

const abrirEdicao = credencial => {
  resultadoDoTeste.value = null;
  emEdicao.value = {
    ...credencial,
    chave_api: '',
    configuracao: { ...(credencial.configuracao || {}) },
  };
};

const ajustarFinalidade = () => {
  if (!finalidadesDisponiveis.value.includes(emEdicao.value.finalidade)) {
    emEdicao.value.finalidade = 'texto';
  }
};

const salvar = async () => {
  const credencial = emEdicao.value;
  salvando.value = true;

  const dados = credencial.id
    ? await chamar(assistenteIaApi.editarCredencial(credencial.id, credencial))
    : await chamar(assistenteIaApi.criarCredencial(credencial));

  salvando.value = false;
  if (!dados) return;

  emEdicao.value = null;
  carregar();
};

const testar = async credencial => {
  testando.value = credencial.id;
  resultadoDoTeste.value = null;

  const dados = await chamar(assistenteIaApi.testarCredencial(credencial.id));
  testando.value = null;
  // O `carregar()` apagava o erro do teste na hora — o C1 da AIA-07, que continuava aqui
  // (AIA-09, C3). Sem resposta, a lista continua a mesma e o erro fica na tela.
  if (!dados) return;

  resultadoDoTeste.value = { ...dados.payload, id: credencial.id };
  carregar();
};

const apagar = async () => {
  const alvo = paraApagar.value;
  paraApagar.value = null;
  if (!alvo) return;

  const feito = await chamar(assistenteIaApi.apagarCredencial(alvo.id));
  if (feito !== null) carregar();
};

const alternarAtiva = async credencial => {
  const feito = await chamar(
    assistenteIaApi.editarCredencial(credencial.id, { ativa: !credencial.ativa })
  );
  if (feito === null) return;

  carregar();
};

onMounted(carregar);
</script>

<template>
  <section class="flex flex-col gap-4">
    <Banner v-if="erro" color="ruby">{{ erro }}</Banner>

    <Banner
      v-if="resultadoDoTeste"
      :color="resultadoDoTeste.ok ? 'teal' : 'ruby'"
    >
      {{
        resultadoDoTeste.ok
          ? `${t('ASSISTENTE_IA.CREDENCIAIS.TESTE_OK')} (${resultadoDoTeste.latencia_ms} ms)`
          : resultadoDoTeste.erro
      }}
    </Banner>

    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-heading-2 text-n-slate-12">
          {{ t('ASSISTENTE_IA.CREDENCIAIS.TITULO') }}
        </h2>
        <p class="mb-0 text-n-slate-11">
          {{ t('ASSISTENTE_IA.CREDENCIAIS.DESCRICAO') }}
        </p>
      </div>
      <Button
        :label="t('ASSISTENTE_IA.CREDENCIAIS.NOVA')"
        icon="i-lucide-plus"
        size="sm"
        @click="abrirNova"
      />
    </div>

    <div
      v-if="emEdicao"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="grid gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('ASSISTENTE_IA.CREDENCIAIS.PROVEDOR') }}
          <select
            v-model="emEdicao.provedor"
            class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
            @change="ajustarFinalidade"
          >
            <option value="anthropic">Anthropic</option>
            <option value="openai">OpenAI</option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('ASSISTENTE_IA.CREDENCIAIS.FINALIDADE') }}
          <select
            v-model="emEdicao.finalidade"
            class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
          >
            <option v-for="f in finalidadesDisponiveis" :key="f" :value="f">
              {{
                f === 'texto'
                  ? t('ASSISTENTE_IA.CREDENCIAIS.TEXTO')
                  : t('ASSISTENTE_IA.CREDENCIAIS.VETORIZACAO')
              }}
            </option>
          </select>
          <span v-if="emEdicao.provedor !== 'openai'" class="text-n-slate-11">
            {{ t('ASSISTENTE_IA.CREDENCIAIS.SO_OPENAI') }}
          </span>
        </label>
      </div>

      <Input
        v-model="emEdicao.rotulo"
        :label="t('ASSISTENTE_IA.CREDENCIAIS.ROTULO')"
      />
      <!-- autocomplete="new-password" e name proprio: sem isso o navegador oferece a SENHA do
           login do Chatwoot aqui, e "deixe em branco para manter" vira "trocar a chave do
           provedor pela senha do usuario" (AIA-07, C6). -->
      <Input
        v-model="emEdicao.chave_api"
        type="password"
        name="assistente-ia-chave-api"
        autocomplete="new-password"
        :label="t('ASSISTENTE_IA.CREDENCIAIS.CHAVE')"
        :placeholder="
          emEdicao.id ? t('ASSISTENTE_IA.CREDENCIAIS.CHAVE_MANTER') : ''
        "
      />
      <Input
        v-model="emEdicao.configuracao.modelo_padrao"
        :label="t('ASSISTENTE_IA.CREDENCIAIS.MODELO_PADRAO')"
      />
      <Input
        v-if="emEdicao.provedor === 'openai'"
        v-model="emEdicao.configuracao.endpoint"
        :label="t('ASSISTENTE_IA.CREDENCIAIS.ENDPOINT')"
        :message="t('ASSISTENTE_IA.CREDENCIAIS.ENDPOINT_AJUDA')"
      />

      <div class="flex gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.SALVAR')"
          :is-loading="salvando"
          size="sm"
          @click="salvar"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="emEdicao = null"
        />
      </div>
    </div>

    <div v-if="carregando && !credenciais.length" class="flex justify-center py-16">
      <Spinner />
    </div>

    <TabelaVazia
      v-else-if="!credenciais.length"
      :mensagem="t('ASSISTENTE_IA.CREDENCIAIS.VAZIO')"
      :acao-label="t('ASSISTENTE_IA.CREDENCIAIS.NOVA')"
      @acao="abrirNova"
    />

    <table v-else class="w-full text-sm text-left">
      <thead class="border-b text-n-slate-11 border-n-weak">
        <tr>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CREDENCIAIS.ROTULO') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CREDENCIAIS.PROVEDOR') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CREDENCIAIS.FINALIDADE') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CREDENCIAIS.CHAVE') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CREDENCIAIS.ATIVA') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CREDENCIAIS.EM_USO') }}</th>
          <th class="py-2 font-medium text-right">
            {{ t('ASSISTENTE_IA.ASSISTENTES.ACOES') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="credencial in credenciais"
          :key="credencial.id"
          class="border-b border-n-weak"
        >
          <td class="py-3 text-n-slate-12">{{ credencial.rotulo }}</td>
          <td class="py-3 text-n-slate-11">{{ credencial.provedor }}</td>
          <td class="py-3 text-n-slate-11">
            {{
              credencial.finalidade === 'texto'
                ? t('ASSISTENTE_IA.CREDENCIAIS.TEXTO')
                : t('ASSISTENTE_IA.CREDENCIAIS.VETORIZACAO')
            }}
          </td>
          <td class="py-3 font-mono text-n-slate-11">
            {{ credencial.chave_final || t('ASSISTENTE_IA.COMUM.NAO_INFORMADO') }}
          </td>
          <td class="py-3">
            <button
              class="px-2 py-0.5 text-xs rounded-full"
              :class="
                credencial.ativa
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
              @click="alternarAtiva(credencial)"
            >
              {{
                credencial.ativa
                  ? t('ASSISTENTE_IA.COMUM.ATIVO')
                  : t('ASSISTENTE_IA.COMUM.INATIVO')
              }}
            </button>
          </td>
          <td class="py-3 text-n-slate-11">
            {{
              credencial.em_uso_por.map(a => a.nome).join(', ') ||
              t('ASSISTENTE_IA.COMUM.NAO_INFORMADO')
            }}
          </td>
          <td class="py-3">
            <div class="flex justify-end gap-2">
              <Button
                :label="t('ASSISTENTE_IA.CREDENCIAIS.TESTAR')"
                variant="faded"
                color="slate"
                size="xs"
                :is-loading="testando === credencial.id"
                @click="testar(credencial)"
              />
              <button
                class="text-n-blue-11 hover:underline"
                @click="abrirEdicao(credencial)"
              >
                {{ t('ASSISTENTE_IA.COMUM.EDITAR') }}
              </button>
              <button
                class="text-n-ruby-11 hover:underline"
                @click="paraApagar = credencial"
              >
                {{ t('ASSISTENTE_IA.COMUM.APAGAR') }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="paraApagar"
      class="flex flex-col gap-2 p-4 border rounded-xl border-n-ruby-4 bg-n-ruby-2"
    >
      <p class="mb-0 font-medium text-n-slate-12">
        {{ t('ASSISTENTE_IA.CREDENCIAIS.APAGAR_TITULO') }}
      </p>
      <p class="mb-0 text-n-slate-11">
        {{ t('ASSISTENTE_IA.CREDENCIAIS.APAGAR_TEXTO') }}
      </p>
      <div class="flex gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.APAGAR')"
          color="ruby"
          size="sm"
          @click="apagar"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="paraApagar = null"
        />
      </div>
    </div>
  </section>
</template>
